import { canAccessFeature, TierFeatureKey } from '../constants/planFeatures';
import type { Organization } from '../types/organization';

/** Fixed path prefix for org public pages on the app host. */
export const ORG_PUBLIC_PATH_PREFIX = '/club-academy' as const;

export type OrgPublicUrlEnv = 'production' | 'staging' | 'development';

export interface OrgPublicUrlOptions {
  env?: OrgPublicUrlEnv;
  webAppOrigin?: string;
  subdomainBaseDomain?: string;
}

export type OrgPublicHostMode = 'path' | 'subdomain';

function normalizeOrigin(origin: string): string {
  return origin.replace(/\/$/, '');
}

function isLocalDevHostname(host: string): boolean {
  return host === 'localhost' || host.endsWith('.localhost');
}

function isStagingHostname(host: string): boolean {
  return host === 'dev.courtsync.io' || host.endsWith('.dev.courtsync.io');
}

function inferEnv(): OrgPublicUrlEnv {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    const web = process.env.WEB_APP_URL ?? '';
    if (web.includes('dev.') || web.includes('-dev')) return 'staging';
    return 'production';
  }
  if (typeof globalThis !== 'undefined') {
    const loc = (globalThis as { location?: { hostname?: string } }).location;
    const host = loc?.hostname;
    if (host) {
      if (isLocalDevHostname(host)) return 'development';
      if (isStagingHostname(host)) return 'staging';
      return 'production';
    }
  }
  return 'development';
}

function defaultWebAppOrigin(env: OrgPublicUrlEnv): string {
  switch (env) {
    case 'production':
      return 'https://app.courtsync.io';
    case 'staging':
      return 'https://app.dev.courtsync.io';
    default: {
      const origin = (globalThis as { location?: { origin?: string } }).location?.origin;
      return origin ? normalizeOrigin(origin) : 'http://localhost:3000';
    }
  }
}

function defaultSubdomainBaseDomain(env: OrgPublicUrlEnv): string {
  switch (env) {
    case 'production':
      return 'courtsync.io';
    case 'staging':
      return 'dev.courtsync.io';
    default:
      return 'localhost';
  }
}

export function orgSubdomainBaseDomain(options: OrgPublicUrlOptions = {}): string {
  const env = options.env ?? inferEnv();
  return options.subdomainBaseDomain ?? defaultSubdomainBaseDomain(env);
}

export function formatOrgSubdomainHost(label: string, options: OrgPublicUrlOptions = {}): string {
  const env = options.env ?? inferEnv();
  const baseDomain = orgSubdomainBaseDomain(options);
  const normalized = label.trim().toLowerCase();
  if (env === 'development' && baseDomain === 'localhost') {
    const port = (globalThis as { location?: { port?: string } }).location?.port;
    const portSuffix = port ? `:${port}` : ':3000';
    return `${normalized}.localhost${portSuffix}`;
  }
  return `${normalized}.${baseDomain}`;
}

export function isOrgBrandedSubdomainActive(
  org: Pick<Organization, 'subdomain' | 'subdomainEnabled'>,
  subscriptionTier: string = 'starter',
  clubTier?: string | null
): boolean {
  if (!org.subdomain?.trim() || org.subdomainEnabled !== true) {
    return false;
  }
  if (clubTier && canAccessFeature(clubTier as 'starter' | 'professional' | 'enterprise', TierFeatureKey.BRANDED_SUBDOMAIN)) {
    return true;
  }
  return canAccessFeature(subscriptionTier as 'starter' | 'professional' | 'enterprise', TierFeatureKey.BRANDED_SUBDOMAIN);
}

export function orgPublicOrigin(
  org: Pick<Organization, 'slug' | 'subdomain' | 'subdomainEnabled'>,
  options: OrgPublicUrlOptions = {},
  brandedActive = false
): string {
  const env = options.env ?? inferEnv();
  const webAppOrigin = normalizeOrigin(options.webAppOrigin ?? defaultWebAppOrigin(env));
  const baseDomain = options.subdomainBaseDomain ?? defaultSubdomainBaseDomain(env);

  if (brandedActive && org.subdomain?.trim()) {
    const label = org.subdomain.trim().toLowerCase();
    if (env === 'development' && baseDomain === 'localhost') {
      const port = (globalThis as { location?: { port?: string } }).location?.port;
      const portSuffix = port ? `:${port}` : ':3000';
      return `http://${label}.localhost${portSuffix}`;
    }
    return `https://${label}.${baseDomain}`;
  }

  return webAppOrigin;
}

export function orgPublicPathPrefix(
  org: Pick<Organization, 'slug'>,
  hostMode: OrgPublicHostMode = 'path'
): string {
  if (hostMode === 'subdomain') {
    return '';
  }
  return `${ORG_PUBLIC_PATH_PREFIX}/${org.slug}`;
}

export function orgPublicPath(
  org: Pick<Organization, 'slug'>,
  subpath: string,
  hostMode: OrgPublicHostMode = 'path'
): string {
  const prefix = orgPublicPathPrefix(org, hostMode);
  const normalized = subpath.startsWith('/') ? subpath : `/${subpath}`;
  if (!prefix) return normalized;
  return `${prefix}${normalized}`;
}

export function orgPublicBaseUrl(
  org: Pick<Organization, 'slug' | 'subdomain' | 'subdomainEnabled'>,
  subpath = '',
  options: OrgPublicUrlOptions = {},
  hostMode: OrgPublicHostMode = 'path',
  brandedActive = false
): string {
  const effectiveHostMode: OrgPublicHostMode = brandedActive ? 'subdomain' : hostMode;
  const origin = orgPublicOrigin(org, options, brandedActive);
  const path = subpath
    ? orgPublicPath(org, subpath, effectiveHostMode)
    : orgPublicPathPrefix(org, effectiveHostMode);
  return path ? `${origin}${path}` : origin;
}
